import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "@similarweb/ui-components";
import { Checkbox } from "@similarweb/ui-components";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import { selectExcludeLists } from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";
import type { ExcludeListType } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import {
    modalStyles,
    StyledDisclaimer,
    StyledLegend,
    StyledList,
    StyledModalFooter,
    StyledSubtitle,
    StyledTitle,
} from "./styles";

type Props = {
    isOpen: boolean;
    onClose(): void;
    onExclude(listsToExclude: readonly ExcludeListType[]): void;
};

const ExcludeDuplicateOpportunitiesModal = (props: Props) => {
    const { isOpen, onClose, onExclude } = props;

    const [listsToExclude, setListsToExclude] = useState<ExcludeListType[]>([]);

    const { distinctDuplicatesCount, lists: excludeOptions } = useSelector(selectExcludeLists);

    useEffect(() => {
        setListsToExclude(excludeOptions.map(({ listType }) => listType));
    }, [excludeOptions]);

    const isOptionSelected = (optionKey: ExcludeListType) => listsToExclude.includes(optionKey);

    const handleModalClose = () => {
        onClose();
    };

    const handleExcludeSubmit = () => {
        onExclude(listsToExclude);

        handleModalClose();
    };

    const handleExcludeOptionClick = (selectedListId: ExcludeListType) => {
        setListsToExclude((listsToExclude) => {
            return listsToExclude.includes(selectedListId)
                ? listsToExclude.filter((listId) => listId !== selectedListId)
                : [...listsToExclude, selectedListId];
        });
    };

    return (
        <ProModal
            isOpen={isOpen}
            showCloseIcon={false}
            shouldCloseOnOverlayClick={false}
            customStyles={modalStyles}
        >
            <I18n
                element={StyledTitle}
                translatable={{ key: "si.opportunity-list-exclude-websites-modal.title" }}
                data-automation="modal-title-text"
            />

            <I18n
                element={StyledSubtitle}
                data-automation="modal-subtitle-text"
                translatable={{
                    key: "si.opportunity-list-exclude-websites-modal.subtitle",
                    replacements: {
                        numberOfDuplicateWebsites: distinctDuplicatesCount,
                    },
                }}
            />

            <I18n
                element={StyledLegend}
                data-automation="modal-options-title-text"
                translatable={{ key: "si.opportunity-list-exclude-websites-modal.options.title" }}
            />
            <StyledList>
                {excludeOptions.map(({ listType, duplicatesCount }) => (
                    <li key={listType}>
                        <Checkbox
                            selected={isOptionSelected(listType)}
                            label={translate(`si.opportunity-list-exclude-websites-modal.options.${listType}`, {
                                numberOfWebsites: duplicatesCount,
                            })}
                            onClick={() => handleExcludeOptionClick(listType)}
                        />
                    </li>
                ))}
            </StyledList>

            <I18n
                element={StyledDisclaimer}
                data-automation="modal-disclaimer-text"
                translatable={{ key: "si.opportunity-list-exclude-websites-modal.disclaimer" }}
            />

            <StyledModalFooter>
                <Button
                    onClick={handleModalClose}
                    type="flat"
                >
                    {translate("si.common.button.cancel")}
                </Button>
                <Button onClick={handleExcludeSubmit}>{translate("si.common.button.continue")}</Button>
            </StyledModalFooter>
        </ProModal>
    );
};

export default ExcludeDuplicateOpportunitiesModal;
